import { EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import type { Node as PMNode, Slice } from '@atlaskit/editor-prosemirror/model';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';
import { convertProsemirrorToMarkdown } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown';
import type { IDMap } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown/serializer';
import type { MentionMap } from '@atlassian/ai-model-io/utils/mention-map';
import type { ContentStatistics } from '@atlassian/ai-model-io/utils/prosemirror-content-statistics';
import { ErrorUtils } from '@atlassian/editor-ai-common/utils/errors';

import {
	mergeNonErrorFallbackState,
	debounceDispatchNonErrorFallbackStateAnalyticsEvent,
} from '../../../utils/analytics/nonErrorFallbackAnalytics';
import { addAnalytics } from '../../../utils/analytics/utils';
import { enableComplexNodes } from '../../../utils/get-pm-fragment-with-fallback';
import type { UpdateIdMap } from '../config-items';

import { getContextSelectionScope } from './get-context-selection-scope';

type SliceToMarkdownProps = {
	// Optional used to enable and track the user journey
	aiSessionId?: string;
	/**
	 * @default markdown
	 */
	convertTo: 'markdown-plus' | 'markdown';
	editorView: EditorView;
	existingIdMap?: IDMap;
	mentionMap?: MentionMap;
	node?: undefined;
	slice: Slice;
	updateIdMap: UpdateIdMap;
};
type NodeToMarkdownProps = {
	// Optional used to enable and track the user journey
	aiSessionId?: string;
	/**
	 * @default markdown
	 */
	convertTo: 'markdown-plus' | 'markdown';
	editorView: EditorView;
	existingIdMap?: IDMap;
	mentionMap?: MentionMap;
	node: PMNode;
	slice?: undefined;
	updateIdMap: UpdateIdMap;
};
type SliceOrNodeToMarkdownProps = SliceToMarkdownProps | NodeToMarkdownProps;

/**
 * Takes a prosemirror schema and (slice or node) and returns it as
 * markdown / markdown plus.
 */
export function sliceOrNodeToMarkdown({
	slice,
	node,
	editorView,
	convertTo,
	existingIdMap,
	updateIdMap,
	mentionMap,
	aiSessionId,
}: SliceOrNodeToMarkdownProps): {
	contentStatistics?: ContentStatistics;
	idMap: IDMap;
	markdown: string;
} {
	const fragment = (slice || node).content;
	try {
		// we track whether the context is a whole document or a partial selection
		const contextSelectionScope = getContextSelectionScope({
			editorView,
			slice,
			node,
		});
		if (convertTo === 'markdown-plus') {
			const { markdown, idMap, contentStatistics, fallbackState, nonErrorFallbackState } =
				convertProsemirrorToMarkdown({
					editorView,
					fragment,
					featureToggles: {
						markdownPlus: true,
						markdownPlusExtensions: true,
						markdownPlusPanels: true,
						markdownPlusDecisions: true,
						markdownPlusAttributeTest: fg('platform_editor_ai_markdown_plus_attr_test'),
						markdownPlusMentions: fg('platform_editor_ai_draft_reply_mentions'),
						markdownPlusAddComplexNodes: enableComplexNodes(),
						markdownPlusUseNextMediaPlugin: fg('platform_editor_ai_next_media_plugin'),
						allowAttributesMatchDefault: true,
					},
					mentionMap: mentionMap,
					idMap: existingIdMap,
				});

			if (!!Object.keys(nonErrorFallbackState).length && aiSessionId) {
				mergeNonErrorFallbackState(nonErrorFallbackState);
				debounceDispatchNonErrorFallbackStateAnalyticsEvent(editorView, aiSessionId);
			}

			if (!!Object.keys(fallbackState).length) {
				const tr = addAnalytics({
					editorState: editorView.state,
					tr: editorView.state.tr,
					payload: {
						action: 'fallback',
						actionSubject: 'editorPluginAI',
						actionSubjectId: 'markdownSerializer',
						attributes: {
							fallbackState,
						},
						eventType: EVENT_TYPE.OPERATIONAL,
					},
				});

				tr.setMeta('addToHistory', false);

				editorView.dispatch(tr);
			}

			// This ID map is used to map the markdown back to the original ProseMirror
			// when we receive transformed content back from the llm.
			updateIdMap({
				idMap,
			});

			return {
				idMap,
				markdown,
				contentStatistics: { ...contentStatistics, contextSelectionScope },
			};
		}

		const { markdown, idMap, contentStatistics } = convertProsemirrorToMarkdown({
			editorView,
			fragment,
			featureToggles: {},
			mentionMap: mentionMap,
			idMap: existingIdMap,
		});

		return {
			idMap,
			markdown,
			contentStatistics: { ...contentStatistics, contextSelectionScope },
		};
	} catch (e) {
		const tr = addAnalytics({
			editorState: editorView.state,
			tr: editorView.state.tr,
			payload: {
				action: 'failed',
				actionSubject: 'editorPluginAI',
				actionSubjectId: 'markdownSerializer',
				attributes: {
					errorType: 'markdownSerializer',
					errorMessage: ErrorUtils.extractErrorMessage(e),
				},
				eventType: EVENT_TYPE.OPERATIONAL,
			},
		});

		editorView.dispatch(tr);

		return { idMap: {}, markdown: '', contentStatistics: undefined };
	}
}
